import { generateCustomEndpoint } from "@/utils/utils";
import type { PaginatedResponse } from "@/v2/queries/types";
import { apiClientJSON } from "../apiClient";

export interface Station {
  id: number;
  code: string;
  name: string;
  status: string;
  type: string;
  latitude: number;
  longitude: number;
  cityId: number;
  cityName: string;
  address: string;
  description: string;
  totalDocks: number;
  availableBikes: number;
  availableDocks: number;
  faultyDocks: number;
  createdAt: string;
  updatedAt: string;
  // Additional properties from API
  brand?: string;
  lastSeenOnline?: string | null;
  dockCount?: number;
  vehicleCount?: number;
  faultyDockCount?: number;
  partnerId?: number | null;
}

export interface SpeedLimitZone {
  id: number;
  name: string;
  cityId: number;
  cityName: string;
  speedLimit: number;
  geometry: object;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface StationListParams {
  page?: number;
  search?: string;
  cityId?: number | null;
  sort?: string;
  filter?: string;
  size?: number;
}

export interface StationCreate {
  name: string;
  address?: string;
  latitude: number;
  longitude: number;
  cityId: number;
  status: string;
  type?: string;
  [key: string]: unknown;
}

export interface StationUpdate extends Partial<StationCreate> {
  [key: string]: unknown;
}

export interface SpeedLimitZoneCreate {
  name: string;
  cityId: number;
  speedLimit: number;
  geometry: object;
  status: string;
  [key: string]: unknown;
}

export interface SpeedLimitZoneUpdate extends Partial<SpeedLimitZoneCreate> {
  [key: string]: unknown;
}

export const getStations = (
  params: StationListParams = {}
): Promise<PaginatedResponse<Station>> => {
  const {
    page = 0,
    search = "",
    cityId = null,
    sort = "",
    filter = "",
    size = 10,
  } = params;
  const endpoint = generateCustomEndpoint(
    "/station-service/dashboard/stations?",
    page,
    search,
    cityId ? String(cityId) : "",
    sort,
    filter,
    String(size)
  );
  return apiClientJSON.get(endpoint).then((r) => r.data);
};

export const getStationById = (id: number): Promise<Station> => {
  return apiClientJSON
    .get(`/station-service/dashboard/stations/${id}`)
    .then((r) => r.data);
};

export const createStation = (dto: StationCreate): Promise<Station> => {
  return apiClientJSON
    .post("/station-service/dashboard/stations", dto)
    .then((r) => r.data);
};

export const updateStation = (
  id: number,
  dto: StationUpdate
): Promise<Station> => {
  // The backend's update endpoint is tri-state on partnerId:
  //   partnerId: 5            → assign partner 5
  //   partnerId: null + ?clearPartner=true → clear partner
  //   partnerId: null (alone) → no-op (leave existing partner unchanged)
  // The FE always intends the body to be the source of truth, so when we send
  // partnerId: null we have to opt in to the clearPartner query param.
  const params = dto.partnerId === null ? "?clearPartner=true" : "";
  return apiClientJSON
    .put(`/station-service/dashboard/stations/${id}${params}`, dto)
    .then((r) => r.data);
};

export const getStationStatuses = (): Promise<string[]> => {
  return apiClientJSON
    .get("/station-service/dashboard/stations/statuses")
    .then((r) => r.data);
};

export const getStationVehicleStatuses = (): Promise<string[]> => {
  return apiClientJSON
    .get("/station-service/dashboard/stations/vehicles/statuses")
    .then((r) => r.data);
};

export const refreshStation = (id: number): Promise<void> => {
  return apiClientJSON
    .post(`/station-service/dashboard/stations/${id}/refresh`)
    .then(() => {});
};

export const reloginStation = (id: number): Promise<void> => {
  return apiClientJSON
    .post(`/station-service/dashboard/stations/${id}/relogin`)
    .then(() => {});
};

export const clearAllStationFaults = (id: number): Promise<void> => {
  return apiClientJSON
    .post(`/station-service/dashboard/stations/${id}/clear-all-faults`)
    .then(() => {});
};

export const getSpeedLimitZones = (
  params: StationListParams = {}
): Promise<PaginatedResponse<SpeedLimitZone>> => {
  const {
    page = 0,
    search = "",
    cityId = null,
    sort = "",
    filter = "",
    size = 10,
  } = params;
  const endpoint = generateCustomEndpoint(
    "/station-service/dashboard/limit/zone?",
    page,
    search,
    cityId ? String(cityId) : "",
    sort,
    filter,
    String(size)
  );
  return apiClientJSON.get(endpoint).then((r) => r.data);
};

export const getSpeedLimitZoneById = (id: number): Promise<SpeedLimitZone> => {
  return apiClientJSON
    .get(`/station-service/dashboard/limit/zone/${id}`)
    .then((r) => r.data);
};

export const createSpeedLimitZone = (
  dto: SpeedLimitZoneCreate
): Promise<SpeedLimitZone> => {
  return apiClientJSON
    .post("/station-service/dashboard/limit/zone/", dto)
    .then((r) => r.data);
};

export const updateSpeedLimitZone = (
  id: number,
  dto: SpeedLimitZoneUpdate
): Promise<SpeedLimitZone> => {
  return apiClientJSON
    .put(`/station-service/dashboard/limit/zone/${id}`, dto)
    .then((r) => r.data);
};

export interface StationListItem {
  id: number;
  code: string;
  name: string;
  latitude: number;
  longitude: number;
  type: string;
  status: string;
}

export interface DockListItem {
  id: number;
  code: string;
  stationId: number;
  status: string;
}

function normalizeListCodes<T extends { code: string }>(items: T[]): T[] {
  return items.map((item) => {
    const code: unknown = item.code;
    if (
      !(typeof code === "string" && code.length > 0) &&
      !(typeof code === "number" && Number.isSafeInteger(code))
    )
      throw new Error("Invalid asset code");
    return { ...item, code: String(code) };
  });
}

export const getStationsByCity = (
  cityId: number | null
): Promise<StationListItem[]> => {
  const endpoint = `/station-service/dashboard/stations/list${cityId ? `?cityId=${cityId}` : ""}`;
  return apiClientJSON
    .get(endpoint)
    .then((r) => normalizeListCodes<StationListItem>(r.data));
};

export const getDocksByCity = (
  cityId: number | null
): Promise<DockListItem[]> => {
  const endpoint = `/station-service/dashboard/docks/list${cityId ? `?cityId=${cityId}` : ""}`;
  return apiClientJSON
    .get(endpoint)
    .then((r) => normalizeListCodes<DockListItem>(r.data));
};
